/**
 * NCR Edit collection / credit / replacement workflow handlers, split out of
 * useNCREdit.
 */

import { qualityAPI, NonConformanceReport } from '../../lib/api';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../lib/api/types/common';
import type { InitiateCollectionData } from './CustomerProcessRequests';

interface UseNCRWorkflowHandlersParams {
  /** NCR id from the route; the handlers no-op until it resolves. */
  id: string | undefined;
  setSaving: (saving: boolean) => void;
  /** Refetch the NCR; called with `true` so the Tabs tree stays mounted. */
  fetchNCRDetails: (isRefresh?: boolean) => Promise<NonConformanceReport | undefined>;
  /** E21: after a customer's approval, offer to close if nothing else is open. */
  offerCloseIfLast: (fresh: NonConformanceReport | undefined) => void;
}

/**
 * Confirm / initiate / update actions for the multi-step Collection, Credit
 * and Replacement processes on the NCR edit page.
 *
 * @param params - route id, the saving setter, the NCR refetch callback and
 *   the offer-to-close callback owned by useNCREdit
 * @returns the confirm, initiate, assign, submit and update-status handlers
 *   for the collection, credit and replacement workflows
 */
export function useNCRWorkflowHandlers({ id, setSaving, fetchNCRDetails, offerCloseIfLast }: UseNCRWorkflowHandlersParams) {
  const handleConfirmCollection = async () => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.confirmCollection(parseInt(id));
      toast.success('Collection confirmed');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error confirming collection:', error);
      toast.error('Failed to confirm collection');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmCredit = async () => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.confirmCredit(parseInt(id));
      toast.success('Credit confirmed');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error confirming credit:', error);
      toast.error('Failed to confirm credit');
    } finally {
      setSaving(false);
    }
  };

  // Multi-step Collection Process handlers
  const handleInitiateCollection = async (data: InitiateCollectionData) => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.initiateCollection(parseInt(id), data);
      toast.success('Collection process initiated');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error initiating collection:', error);
      // CR-5538: surface the backend's actual message ("Only the company
      // that raised the NCR can initiate collection", "Collection is not
      // required for this NCR", etc.) instead of swallowing it as a
      // generic "Failed to initiate collection" toast.
      toast.error(getApiErrorMessage(error, 'Failed to initiate collection'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCollectionStatus = async (newStatus: string, data?: Record<string, unknown>) => {
    if (!id) return;
    try {
      setSaving(true);
      const res = await qualityAPI.updateCollectionStatus(parseInt(id), {
        status: newStatus,
        ...data,
      } as Parameters<typeof qualityAPI.updateCollectionStatus>[1]);
      // CR-8720: a supplier's "confirm" with a different date lands at
      // 'date_proposed', so the toast follows the status reached.
      const reached = res.data?.status ?? newStatus;
      const COLLECTION_TOASTS: Record<string, string> = {
        // CR-8720: the supplier's counter and the customer's answer.
        date_proposed: 'Collection date proposed to the customer',
        pending: 'New collection date proposed to the supplier',
        confirmed: 'Collection date confirmed',
        collected: 'Collection confirmed',
        completed: 'Receipt confirmed. Collection complete',
      };
      toast.success(COLLECTION_TOASTS[reached] ?? 'Collection updated');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error updating collection status:', error);
      // CR-6073 rework: this handler was the last of the collection/credit/
      // replacement update-status actions still swallowing the backend's
      // reason ("Cannot transition collection from 'X' to 'Y'", "Only the
      // company that raised the NCR can complete collection", etc.) behind
      // a generic toast. Mirrors the getApiErrorMessage pattern already
      // used by handleUpdateCreditStatus / handleUpdateReplacementStatus.
      toast.error(getApiErrorMessage(error, 'Failed to update collection'));
    } finally {
      setSaving(false);
    }
  };

  /**
   * CR-7352: hand the collection to someone, or clear its owner.
   *
   * @param assignedTo - the user id to assign to, or null to unassign
   */
  const handleAssignCollection = async (assignedTo: number | null) => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.assignCollection(parseInt(id), assignedTo);
      toast.success(assignedTo ? 'Collection assigned' : 'Collection owner cleared');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error assigning collection:', error);
      // Same treatment as its siblings: the backend's own reason ("This
      // collection is already completed", "Assign the collection to an
      // active user in your own company") is the useful message.
      toast.error(getApiErrorMessage(error, 'Failed to assign collection'));
    } finally {
      setSaving(false);
    }
  };

  // Multi-step Credit Process handlers
  const handleInitiateCredit = async (data: {
    credit_amount?: number;
    credit_currency?: string;
    credit_reason?: string;
    customer_notes?: string;
  }) => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.initiateCredit(parseInt(id), data);
      toast.success('Credit process initiated');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error initiating credit:', error);
      // CR-5539: surface the backend's actual message instead of generic
      // "Failed to initiate credit". Same pattern as CR-5538.
      toast.error(getApiErrorMessage(error, 'Failed to initiate credit'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitCreditNote = async (data: FormData) => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.submitCreditNote(parseInt(id), data);
      toast.success('Credit note submitted');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error submitting credit note:', error);
      toast.error(getApiErrorMessage(error, 'Failed to submit credit note'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCreditStatus = async (newStatus: string, data?: Record<string, unknown>) => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.updateCreditStatus(parseInt(id), {
        status: newStatus,
        ...data,
      } as Parameters<typeof qualityAPI.updateCreditStatus>[1]);
      const labels: Record<string, string> = {
        approved: 'Credit confirmed as received',
        rejected: 'Credit rejected',
      };
      toast.success(labels[newStatus] || 'Credit updated');
      const fresh = await fetchNCRDetails(true);
      if (newStatus === 'approved') offerCloseIfLast(fresh);
    } catch (error) {
      console.error('Error updating credit status:', error);
      toast.error(getApiErrorMessage(error, 'Failed to update credit'));
    } finally {
      setSaving(false);
    }
  };

  // Multi-step Replacement Process handlers (mirror the credit handlers)
  const handleInitiateReplacement = async (data: {
    replacement_qty?: number;
    replacement_reason?: string;
    customer_notes?: string;
  }) => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.initiateReplacement(parseInt(id), data);
      toast.success('Replacement process initiated');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error initiating replacement:', error);
      toast.error(getApiErrorMessage(error, 'Failed to initiate replacement'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitReplacementDispatch = async (data: FormData) => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.submitReplacementDispatch(parseInt(id), data);
      toast.success('Dispatch details submitted');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error submitting despatch details:', error);
      toast.error(getApiErrorMessage(error, 'Failed to submit dispatch details'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateReplacementStatus = async (newStatus: string, data?: Record<string, unknown>) => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.updateReplacementStatus(parseInt(id), {
        status: newStatus,
        ...data,
      } as Parameters<typeof qualityAPI.updateReplacementStatus>[1]);
      const labels: Record<string, string> = {
        approved: 'Replacement receipt confirmed and closed',
        rejected: 'Replacement rejected',
      };
      toast.success(labels[newStatus] || 'Replacement updated');
      const fresh = await fetchNCRDetails(true);
      if (newStatus === 'approved') offerCloseIfLast(fresh);
    } catch (error) {
      console.error('Error updating replacement status:', error);
      toast.error(getApiErrorMessage(error, 'Failed to update replacement'));
    } finally {
      setSaving(false);
    }
  };

  return {
    handleConfirmCollection,
    handleConfirmCredit,
    handleInitiateCollection,
    handleUpdateCollectionStatus,
    handleAssignCollection,
    handleInitiateCredit,
    handleSubmitCreditNote,
    handleUpdateCreditStatus,
    handleInitiateReplacement,
    handleSubmitReplacementDispatch,
    handleUpdateReplacementStatus,
  };
}
